import { LockIcon } from '@chakra-ui/icons';
import { Box, Flex, Text, useColorMode } from '@chakra-ui/react';
import { useSession } from '../../hooks/useSession';

const TOTAL_STEPS = 4;

export interface StepHeaderProps {
    step: number; // 1-based, 1..4
    label: string;
}

// Shared chrome rendered at the top of every flow step:
//  - a centered "Secure linking" lock label
//  - a 4-segment brand-color progress bar (segments 1..step filled)
//  - a "STEP X OF 4 · LABEL" caption
export default function StepHeader({ step, label }: StepHeaderProps) {
    const { colorMode } = useColorMode();
    const isLight = colorMode === 'light';
    const session = useSession();

    // Returning users skip the intro (see security-message's redirect), so the
    // flow is 3 steps, not 4. Renumber off the same signal the redirect uses.
    const skipIntro = !!session?.bettorAccounts?.length;
    const total = skipIntro ? TOTAL_STEPS - 1 : TOTAL_STEPS;
    const current = Math.max(1, skipIntro ? step - 1 : step);

    const trackColor = isLight ? 'border' : 'alphaWhite01';
    const mutedColor = isLight ? 'midnightBlue' : 'white';

    return (
        <Flex
            direction="column"
            align="center"
            gap="10px"
            w="full"
            maxW="320px"
            mx="auto"
            mb="24px"
        >
            <Flex align="center" gap="6px" opacity={0.7}>
                <LockIcon boxSize="12px" color={mutedColor} />
                <Text
                    size="xs"
                    letterSpacing="0.08em"
                    textTransform="uppercase"
                    color={mutedColor}
                >
                    Secure linking
                </Text>
            </Flex>

            <Flex w="full" gap="6px" aria-hidden>
                {Array.from({ length: total }, (_, i) => (
                    <Box
                        key={i}
                        flex="1"
                        h="4px"
                        borderRadius="full"
                        bg={i < current ? 'primary' : trackColor}
                        transition="background-color 0.2s ease"
                    />
                ))}
            </Flex>

            <Text
                size="xs"
                letterSpacing="0.08em"
                textTransform="uppercase"
                opacity={0.6}
                color={mutedColor}
            >
                Step {current} of {total} &middot; {label}
            </Text>
        </Flex>
    );
}

// The "Powered by SharpSports" line shown below the CTA on every flow step.
// Kept here so the copy lives in one place.
export function FlowFooterNote() {
    const { colorMode } = useColorMode();
    const mutedColor = colorMode === 'light' ? 'midnightBlue' : 'white';

    return (
        <Text
            size="xs"
            textAlign="center"
            opacity={0.55}
            color={mutedColor}
            mt="4px"
        >
            🛡 Powered by SharpSports
        </Text>
    );
}
